import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as eventService from "../services/eventService";
import CreateEventModal from "../components/events/CreateEventModal";
import EditEventModal from "../components/events/EditEventModal";
import AssignEventAdminModal from "../components/events/AssignEventAdminModal";

function etiquetaEstado(estado: string) {
  return normalizarEstadoEvento(estado).toUpperCase();
}

function normalizarEstadoEvento(estado: string) {
  if (estado === "BORRADOR" || estado === "CONFIRMADO") return "PLANIFICADO";
  if (estado === "COMPLETADO") return "TERMINADO";
  return estado || "PLANIFICADO";
}

function claseBadgeEstado(estado: string) {
  const normalized = normalizarEstadoEvento(estado);
  if (normalized === "ENVIADO")
    return "bg-emerald-500/20 text-emerald-400 border border-emerald-500/20";
  if (normalized === "PLANIFICADO")
    return "bg-purple-500/20 text-purple-400 border border-purple-500/20";
  if (normalized === "TERMINADO")
    return "bg-blue-500/20 text-blue-400 border border-blue-500/20";
  if (normalized === "CANCELADO")
    return "bg-red-500/20 text-red-400 border border-red-500/20";
  return "bg-amber-500/20 text-amber-400 border border-amber-500/20";
}

function formatoFecha(iso: string) {
  try {
    return new Intl.DateTimeFormat("es-PE", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

// Imagen fija para el fondo de la tarjeta (por ahora siempre la misma)
const EVENT_CARD_IMAGE =
  "https://images.unsplash.com/photo-1529634806980-85c3dd6d34ac?auto=format&fit=crop&w=1400&q=80";

function EventsPage() {
  const [events, setEvents] = useState<eventService.EventoAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const navigate = useNavigate();

  const [q, setQ] = useState("");
  const [status, setStatus] = useState("TODOS");

  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openAssign, setOpenAssign] = useState(false);

  const [selected, setSelected] = useState<eventService.EventoAdmin | null>(null);

  async function cargar() {
    setLoading(true);
    setErrorMsg(null);
    try {
      const data = await eventService.listarEventos();
      setEvents(data);
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : "Error cargando eventos");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return events.filter((ev) => {
      const matchQuery =
        !query ||
        ev.nombre.toLowerCase().includes(query) ||
        ev.slug.toLowerCase().includes(query);

      const matchStatus = status === "TODOS" ? true : normalizarEstadoEvento(ev.estado) === status;
      return matchQuery && matchStatus;
    });
  }, [events, q, status]);

  function abrirEditar(ev: eventService.EventoAdmin) {
    setSelected(ev);
    setOpenEdit(true);
  }

  function abrirAsignar(ev: eventService.EventoAdmin) {
    setSelected(ev);
    setOpenAssign(true);
  }

  async function onCreated(newEvent: eventService.EventoAdmin) {
    setOpenCreate(false);
    setEvents((prev) => [newEvent, ...prev]);
  }

  function onUpdated(updated: eventService.EventoAdmin) {
    setEvents((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
    setSelected(updated);
  }

  async function onAssigned() {
    if (!selected) return;
    const updated = await eventService.obtenerEvento(selected.id);
    setEvents((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
    setSelected(updated);
    setOpenAssign(false);
  }

  return (
    <>
      <div className="p-4 sm:p-6 lg:p-12 max-w-7xl mx-auto w-full flex flex-col gap-8">
        {/* Encabezado */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <h2 className="text-3xl md:text-4xl font-serif font-medium text-slate-900 dark:text-white mb-2">
              Gestión de eventos
            </h2>
            <p className="text-slate-500 dark:text-slate-400">
              Administra bodas, asignaciones y logística.
            </p>
          </div>

          <button
            className="w-full sm:w-auto flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white px-6 py-3 rounded-lg font-medium transition-all shadow-[0_0_15px_rgba(127,25,230,0.25)] active:scale-95"
            type="button"
            onClick={() => setOpenCreate(true)}
          >
            <span className="material-symbols-outlined">add_circle</span>
            Crear evento
          </button>
        </div>

        {/* Buscador + filtros */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 bg-white dark:bg-surface-dark p-4 rounded-xl border border-slate-200 dark:border-white/5 shadow-sm">
          <div className="md:col-span-6 relative">
            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[20px]">
              search
            </span>
            <input
              className="block w-full pl-10 pr-3 py-3 border border-slate-200 dark:border-white/10 rounded-lg leading-5 bg-slate-50 dark:bg-background-dark text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm transition-all shadow-sm"
              placeholder="Buscar por nombre o identificador (slug)..."
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>

          <div className="md:col-span-4 relative">
            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[20px]">
              filter_list
            </span>
            <select
              className="block w-full pl-10 pr-10 py-3 border border-slate-200 dark:border-white/10 rounded-lg leading-5 bg-slate-50 dark:bg-background-dark text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm appearance-none cursor-pointer"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="TODOS">Todos los estados</option>
              <option value="PLANIFICADO">Planificado</option>
              <option value="ENVIADO">Enviado</option>
              <option value="TERMINADO">Terminado</option>
              <option value="CANCELADO">Cancelado</option>
            </select>
            <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-[20px]">
              expand_more
            </span>
          </div>

          <div className="md:col-span-2 flex justify-end">
            <button
              type="button"
              onClick={cargar}
              className="w-full md:w-auto flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold text-white bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition-colors dark:bg-background-dark dark:hover:bg-white/5"
            >
              <span className="material-symbols-outlined text-[18px]">refresh</span>
              Actualizar
            </button>
          </div>
        </div>

        {/* Contenido */}
        {loading && (
          <div className="text-slate-500 dark:text-slate-400">
            Cargando eventos...
          </div>
        )}
        {!loading && errorMsg && <div className="text-red-500">{errorMsg}</div>}

        {!loading && !errorMsg && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {filtered.map((ev) => {
              const admins = ev.admins ?? [];
              const visibles = admins.slice(0, 2);

              return (
                <div
                  key={ev.id}
                  className="bg-white dark:bg-surface-dark border border-slate-200 dark:border-white/5 rounded-xl overflow-hidden hover:border-primary/50 transition-all duration-300 group flex flex-col shadow-sm"
                >
                  {/* Cover con imagen */}
                  <div className="h-44 relative overflow-hidden">
                    <div
                      className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-[1.03]"
                      style={{
                        backgroundImage: `linear-gradient(to top, rgba(25,17,33,0.95), rgba(25,17,33,0.35)), url('${EVENT_CARD_IMAGE}')`,
                      }}
                    />

                    {/* Estado */}
                    <div className="absolute top-4 left-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium backdrop-blur-md ${claseBadgeEstado(
                          ev.estado
                        )}`}
                      >
                        {etiquetaEstado(ev.estado)}
                      </span>
                    </div>

                    {/* Ícono lápiz (editar) */}
                    <div className="absolute top-4 right-4">
                      <button
                        type="button"
                        onClick={() => abrirEditar(ev)}
                        className="size-9 rounded-lg bg-black/30 border border-white/10 hover:bg-white/10 text-white transition-colors flex items-center justify-center"
                        title="Editar evento"
                      >
                        <span className="material-symbols-outlined text-[18px]">
                          edit
                        </span>
                      </button>
                    </div>
                  </div>

                  <div className="p-6 flex flex-col flex-1 gap-5 -mt-10 relative z-10">
                    {/* Header */}
                    <div className="flex flex-col gap-2">
                      <h3 className="text-2xl font-serif font-bold text-slate-900 dark:text-white leading-tight">
                        {ev.nombre}
                      </h3>

                      <div className="flex flex-col gap-2 mt-1">
                        <div className="flex items-center gap-2 text-slate-500 dark:text-slate-300 text-sm">
                          <span className="material-symbols-outlined text-primary text-[18px]">
                            calendar_today
                          </span>
                          <span>{formatoFecha(ev.fechaPrincipal)}</span>
                        </div>

                        <div className="flex items-center gap-2 text-slate-500 dark:text-slate-300 text-sm">
                          <span className="material-symbols-outlined text-primary text-[18px]">
                            event_available
                          </span>
                          <span>RSVP hasta {ev.fechaLimiteRsvp ? formatoFecha(ev.fechaLimiteRsvp) : "sin fecha"}</span>
                        </div>

                        <div className="flex items-center gap-2 text-slate-500 dark:text-slate-300 text-sm">
                          <span className="material-symbols-outlined text-primary text-[18px]">
                            tag
                          </span>
                          <span className="truncate">{ev.slug}</span>
                        </div>
                      </div>
                    </div>

                    <div className="h-px bg-slate-200 dark:bg-white/10 w-full"></div>

                    {/* Responsables */}
                    <div className="rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50/80 dark:bg-background-dark/70 p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="material-symbols-outlined text-primary text-[18px]">groups</span>
                            <span className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold">
                              Responsables
                            </span>
                          </div>
                          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                            {admins.length === 0
                              ? "Asigna usuarios para administrar este evento."
                              : `${admins.length} responsable${admins.length === 1 ? "" : "s"} asignado${admins.length === 1 ? "" : "s"}.`}
                          </p>
                        </div>

                        <button
                          className="shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-surface-dark hover:bg-slate-100 dark:hover:bg-white/5 text-slate-700 dark:text-slate-200 transition-colors"
                          type="button"
                          onClick={() => abrirAsignar(ev)}
                          title="Gestionar responsables"
                        >
                          <span className="material-symbols-outlined text-[18px]">
                            manage_accounts
                          </span>
                          <span className="text-sm font-semibold hidden sm:inline">
                            Gestionar
                          </span>
                        </button>
                      </div>

                      {admins.length === 0 ? (
                        <div className="mt-3 flex items-center gap-2 text-slate-500 dark:text-slate-400">
                          <span className="material-symbols-outlined text-[18px]">person_off</span>
                          <span className="text-sm">Sin responsables asignados</span>
                        </div>
                      ) : (
                        <div className="mt-3 flex flex-col gap-2">
                          {visibles.map((a) => (
                            <div
                              key={a.usuarioId}
                              className="flex items-center gap-3 rounded-lg bg-white dark:bg-surface-dark border border-slate-200 dark:border-white/10 px-3 py-2"
                            >
                              <div className="size-8 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                                <span className="material-symbols-outlined text-primary text-[17px]">person</span>
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-sm text-slate-900 dark:text-white font-medium truncate">
                                  {a.nombre}
                                </p>
                                <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                                  {a.email}
                                </p>
                              </div>
                              <span className="shrink-0 rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[11px] font-semibold">
                                {a.rolEvento}
                              </span>
                            </div>
                          ))}
                          {admins.length > 2 && (
                            <div className="text-xs text-slate-500 dark:text-slate-400 px-1">
                              +{admins.length - 2} responsable{admins.length - 2 === 1 ? "" : "s"} más
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Acciones */}
                    <div className="mt-auto pt-2 flex flex-col gap-3">
                      <button
                        className="w-full h-11 rounded-lg bg-primary/90 hover:bg-primary text-white font-semibold transition-all shadow-lg shadow-primary/20 active:scale-[0.99]"
                        type="button"
                        onClick={() => {
                          navigate(`/home/eventos/${ev.id}/ceremonias`);
                        }}
                      >
                        Gestionar Ceremonias
                      </button>

                      <button
                        className="w-full h-11 rounded-lg bg-gold-accent hover:bg-[#c5a028] text-surface-darker font-bold transition-all shadow-lg shadow-gold-accent/20 active:scale-[0.99]"
                        type="button"
                        onClick={() => {
                          navigate(`/home/eventos/${ev.id}/invitados`);
                        }}
                      >
                        Gestionar invitados
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <CreateEventModal
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        onCreated={onCreated}
      />

      <EditEventModal
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        evento={selected}
        onUpdated={onUpdated}
      />

      <AssignEventAdminModal
        open={openAssign}
        onClose={() => setOpenAssign(false)}
        evento={selected}
        onAssigned={onAssigned}
      />
    </>
  );
}

export default EventsPage;
